import {useCallback, useEffect} from 'react';
import {useStoreStore} from '../store/store';
import {useStoresUIStore} from '../store/storesUIStore';
import {Store} from '../models/store.model';
import {useNotification} from '../../../Services/useNotification';

/**
 * Unified hook for stores management that combines data and UI state
 * This replaces the need for multiple useState calls in components
 */
export const useStoresManagement = () => {
  // Data stores
  const storeStore = useStoreStore();
  const uiStore = useStoresUIStore();
  const {success: showSuccess} = useNotification();

  // Destructure from storeStore
  const {
    stores, statistics, pagination,
    loading, statisticsLoading,
    error, statisticsError,
    fetchStores, fetchStatistics, createStore, updateStore, deleteStore,
    setPagination, refreshData, clearErrors
  } = useStoreStore();

  // Destructure from uiStore
  const {
    activeTab, setActiveTab,
    isDetailsModalOpen, openDetailsModal, closeDetailsModal,
    isEditModalOpen, openEditModal, closeEditModal,
    selectedStoreId, setSelectedStoreId,
    editingStore, setEditingStore,
    deletingStoreId, setDeletingStoreId,
    searchQuery, setSearchQuery,
    paymentStatusFilter, setPaymentStatusFilter,
    cityFilter, setCityFilter,
    districtFilter, setDistrictFilter,
    clearFilters, reset: resetUI
  } = uiStore;

  // Initial data fetch
  useEffect(() => {
    fetchStores();
    fetchStatistics();
  }, [fetchStores, fetchStatistics]);

  // Unified Actions
  const handleDeleteStore = useCallback(async (id: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المتجر؟')) {
      setDeletingStoreId(id);
      try {
        await deleteStore(id);
        showSuccess('تم حذف المتجر بنجاح');
      } catch (error) {
        // Error is already handled by the store
      } finally {
        setDeletingStoreId(null);
      }
    }
  }, [deleteStore, setDeletingStoreId]);

  const handleViewDetails = useCallback((store: Store) => {
    setSelectedStoreId(store.id.toString());
    openDetailsModal();
  }, [setSelectedStoreId, openDetailsModal]);

  /** Open details modal by store id (e.g. when navigating from map with state.openStoreId) */
  const handleOpenDetailsByStoreId = useCallback((id: string) => {
    setSelectedStoreId(id);
    openDetailsModal();
  }, [setSelectedStoreId, openDetailsModal]);

  const handleCloseDetailsModal = useCallback(() => {
    closeDetailsModal();
    setSelectedStoreId(null);
  }, [closeDetailsModal, setSelectedStoreId]);

  const handleEditStore = useCallback((store: Store) => {
    setEditingStore(store);
    openEditModal();
  }, [setEditingStore, openEditModal]);

  const handleCloseEditModal = useCallback(() => {
    closeEditModal();
    setEditingStore(null);
  }, [closeEditModal, setEditingStore]);

  const handleEditSuccess = useCallback(() => {
    closeEditModal();
    setEditingStore(null);
    refreshData();
  }, [closeEditModal, setEditingStore, refreshData]);

  const handleCreateSuccess = useCallback(() => {
    refreshData();
  }, [refreshData]);

  // City/district are filtered server-side (across all pages), so paging and
  // page-size changes must carry the active city/region filters along.
  const handlePageChange = useCallback((newPage: number) => {
    const currentLimit = pagination?.limit || 20;
    fetchStores(newPage, currentLimit, {city: cityFilter || undefined, region: districtFilter || undefined});
  }, [fetchStores, pagination?.limit, cityFilter, districtFilter]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    fetchStores(1, newPageSize, {city: cityFilter || undefined, region: districtFilter || undefined});
  }, [fetchStores, cityFilter, districtFilter]);

  // Selecting a city refetches from page 1 (district is reset via the UI store cascade).
  const handleCityChange = useCallback((city: string) => {
    setCityFilter(city);
    const currentLimit = pagination?.limit || 20;
    fetchStores(1, currentLimit, {city: city || undefined, region: undefined});
  }, [setCityFilter, fetchStores, pagination?.limit]);

  const handleDistrictChange = useCallback((district: string) => {
    setDistrictFilter(district);
    const currentLimit = pagination?.limit || 20;
    fetchStores(1, currentLimit, {city: cityFilter || undefined, region: district || undefined});
  }, [setDistrictFilter, fetchStores, pagination?.limit, cityFilter]);

  // "تصفية" button — re-apply current city/district filters from page 1.
  const handleApplyFilters = useCallback(() => {
    const currentLimit = pagination?.limit || 20;
    fetchStores(1, currentLimit, {city: cityFilter || undefined, region: districtFilter || undefined});
  }, [fetchStores, pagination?.limit, cityFilter, districtFilter]);

  // Reset all stores
  const resetAllStores = useCallback(() => {
    clearErrors();
    resetUI();
  }, [clearErrors, resetUI]);

  return {
    // UI State
    activeTab, setActiveTab,
    isDetailsModalOpen, isEditModalOpen,
    selectedStoreId, editingStore, deletingStoreId,
    searchQuery, setSearchQuery,
    paymentStatusFilter, setPaymentStatusFilter,
    cityFilter, districtFilter,
    handleCityChange, handleDistrictChange, handleApplyFilters,
    clearFilters,

    // Data
    stores, statistics, pagination,

    // Loading States
    loading, statisticsLoading,

    // Error States
    error, statisticsError,

    // Actions
    fetchStores, fetchStatistics, createStore, updateStore,
    handleDeleteStore, handleViewDetails, handleOpenDetailsByStoreId, handleCloseDetailsModal,
    handleEditStore, handleCloseEditModal, handleEditSuccess,
    handleCreateSuccess, handlePageChange, handlePageSizeChange,
    refreshData, resetAllStores
  };
};
